// src/intervenciones/iniciarIntervencion.jsx
import React, { useMemo, useEffect, useRef, useState } from "react";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { ticajeStore } from "../data/ticajeStore";

// ===== MUI =====
import { Box, Paper, Typography, Stack, TextField, Button } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

/**
 * IniciarIntervencion (MUI)
 * - MISMA lógica (offline-first) que tu versión original.
 * - Solo cambia presentación + inputs a MUI.
 */
export const IniciarIntervencion = ({
  onTicajeActualizado,
  onSuccess,
  onError,
  uiLocked = false,
}) => {
  // Catálogos
  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // Selecciones
  const [cliente, setCliente] = useState(""); // id cliente (string)
  const [clienteSeleccionado, setClienteSeleccionado] = useState(""); // id cliente (string)
  const [orden, setOrden] = useState(""); // id OT (string)
  const [centro, setCentro] = useState(""); // id centro (string)

  // Extras
  const [comentarios, setComentarios] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [coordenadas, setCoordenadas] = useState({ latitud: null, longitud: null });
  const [inicio, setInicio] = useState(null);
  const [descripcionOT, setDescripcionOT] = useState("");

  // Flags/Refs
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clienteCambiadoManualmente = useRef(false);
  const limpiarMensajeTimeout = useRef(null);

  // ---------------------------------------------------------------------------
  // Catálogos (offline-first)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let alive = true;

    const loadCatalogos = async () => {
      setLoadingCatalogos(true);
      try {
        const [ords, clis] = await Promise.all([
          ticajeStore.getOrdenes(),
          ticajeStore.getClientes(),
        ]);

        if (!alive) return;
        setOrdenes(Array.isArray(ords) ? ords : []);
        setClientes(Array.isArray(clis) ? clis : []);
      } catch (err) {
        console.error("[IniciarIntervencion] Error cargando catálogos:", err);
        if (!alive) return;
        setOrdenes([]);
        setClientes([]);
      } finally {
        if (alive) setLoadingCatalogos(false);
      }
    };

    loadCatalogos();

    return () => {
      alive = false;
      if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    };
  }, []);

  // Centros por OT, vía store
  const fetchCentrosPorOrden = async (ordenId) => {
    try {
      const data = await ticajeStore.getCentrosPorOrden(ordenId);
      const centrosData = Array.isArray(data) ? data : data?.data || [];
      setCentros(centrosData);
    } catch (err) {
      console.error("[IniciarIntervencion] Error al cargar centros de la OT:", err);
      setCentros([]);
    }
  };

  // ---------------------------------------------------------------------------
  // Reacciones a cambios
  // ---------------------------------------------------------------------------

  // Cuando cambia la OT, derivamos cliente, descripción y centros
  useEffect(() => {
    const ordenSeleccionada = ordenes.find((o) => o.id === Number(orden));
    if (!ordenSeleccionada) {
      setCliente("");
      setClienteSeleccionado("");
      setDescripcionOT("");
      setCentros([]);
      return;
    }

    const cid = String(ordenSeleccionada.cliente_id ?? "");
    setCliente(cid);
    setClienteSeleccionado(cid);
    setDescripcionOT(ordenSeleccionada.descripcion || "");
    fetchCentrosPorOrden(ordenSeleccionada.id);
  }, [orden, ordenes]);

  // Si cambia el cliente manualmente, limpiamos OT/centro/desc
  useEffect(() => {
    if (!clienteSeleccionado) return;

    if (clienteCambiadoManualmente.current) {
      setOrden("");
      setCentro("");
      setDescripcionOT("");
      setCentros([]);
      setCliente(clienteSeleccionado);
    }

    clienteCambiadoManualmente.current = false;
  }, [clienteSeleccionado]);

  // Filtrado de OTs por cliente seleccionado
  const ordenesFiltradas = useMemo(() => {
    if (!clienteSeleccionado) return ordenes;
    return ordenes.filter((o) => o.cliente_id === Number(clienteSeleccionado));
  }, [ordenes, clienteSeleccionado]);

  // ---------------------------------------------------------------------------
  // Opciones para Autocomplete (ordenadas)
  // ---------------------------------------------------------------------------
  const clientesConOT = useMemo(() => {
    const ids = new Set((ordenes || []).map((o) => Number(o.cliente_id)));
    return (clientes || [])
      .filter((c) => ids.has(Number(c.id)))
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes, ordenes]);

  const ordenesOrdenadas = useMemo(() => {
    return (ordenesFiltradas || [])
      .slice()
      .sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""));
  }, [ordenesFiltradas]);

  const centrosOrdenados = useMemo(() => {
    return (centros || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const clienteObj = useMemo(() => {
    return clientesConOT.find((c) => String(c.id) === String(clienteSeleccionado)) || null;
  }, [clientesConOT, clienteSeleccionado]);

  const ordenObj = useMemo(() => {
    return ordenesOrdenadas.find((o) => String(o.id) === String(orden)) || null;
  }, [ordenesOrdenadas, orden]);

  const centroObj = useMemo(() => {
    return centrosOrdenados.find((c) => String(c.id) === String(centro)) || null;
  }, [centrosOrdenados, centro]);

  // ---------------------------------------------------------------------------
  // Utilidades
  // ---------------------------------------------------------------------------
  const calcularDistancia = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const setMensajeTemporal = (txt) => {
    setMensaje(txt);
    if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    limpiarMensajeTimeout.current = setTimeout(() => setMensaje(""), 4500);
  };

  // ---------------------------------------------------------------------------
  // Handler principal: inicia intervención
  // ---------------------------------------------------------------------------
  const capturarUbicacionYCrearTicaje = () => {
    // ⚠️ Evita duplicar inicio si ya hay intervención activa (local o servidor)
    const snap = ticajeStore.snapshot ? ticajeStore.snapshot() : { active: null, local: null };
    const active = snap.active || snap.local;
    if (active && !active?.pendingFinish && !active?.pendingCancel) {
      onError?.("Ya tienes una intervención en curso. Ve a 'Finalizar' para cerrarla.");
      onTicajeActualizado?.();
      return;
    }

    // Validaciones de formulario
    if (!orden || !centro) {
      setMensajeTemporal("Selecciona una orden de trabajo y un centro.");
      return;
    }
    if (isSubmitting || uiLocked) return;

    const centroSeleccionadoNow = centros.find((c) => c.id === Number(centro));
    if (!centroSeleccionadoNow?.latitud || !centroSeleccionadoNow?.longitud) {
      setMensajeTemporal("Este centro no tiene coordenadas asignadas.");
      return;
    }

    if (!navigator.geolocation) {
      onError?.("Tu navegador no soporta geolocalización.");
      return;
    }

    setIsSubmitting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitud = position.coords.latitude;
          const longitud = position.coords.longitude;

          const distancia = calcularDistancia(
            latitud,
            longitud,
            parseFloat(centroSeleccionadoNow.latitud),
            parseFloat(centroSeleccionadoNow.longitud)
          );

          if (distancia > 1) {
            const confirmar = window.confirm(
              `Estás a ${distancia.toFixed(2)} km del centro. ¿Deseas iniciar igualmente?`
            );
            if (!confirmar) {
              setMensajeTemporal("Ticaje cancelado por el usuario.");
              setIsSubmitting(false);
              return;
            }
          }

          const fpInstance = await FingerprintJS.load();
          const result = await fpInstance.get();
          const fingerprint = result.visitorId;

          const clienteIdNum = Number(cliente) || null;
          const centroIdNum = Number(centro) || null;
          const ordenIdNum = Number(orden) || null;

          const cliObj = clientes.find((c) => c.id === clienteIdNum);
          const ordObj = ordenes.find((o) => o.id === ordenIdNum);

          const clienteNombre = cliObj?.nombre || "";
          const centroNombre = centroSeleccionadoNow?.nombre || "";
          const ordenTitulo = ordObj?.titulo || "";

          const resultStore = await ticajeStore.start({
            clienteId: clienteIdNum,
            centroId: centroIdNum,
            ordenId: ordenIdNum,
            ordenTitulo,
            clienteNombre,
            centroNombre,
            comentarios: comentarios || "",
            latitud,
            longitud,
            fingerprint,
          });

          setInicio(new Date());
          setCoordenadas({ latitud, longitud });

          if (resultStore?.status === "online") {
            onSuccess?.("Ticaje iniciado correctamente.");
          } else if (resultStore?.status === "offline") {
            onSuccess?.("Sin conexión: ticaje guardado y se enviará automáticamente.");
          } else if (resultStore?.status === "noop") {
            onError?.("Ya existe una intervención activa, no se ha iniciado otra.");
          } else {
            onSuccess?.("Ticaje iniciado.");
          }

          await onTicajeActualizado?.();
        } catch (error) {
          console.error("❌ Error al iniciar ticaje (store):", error);
          onError?.("Error al registrar el ticaje.");
        } finally {
          setIsSubmitting(false);
        }
      },
      (error) => {
        console.error("❌ Error de geolocalización:", error);
        onError?.("No se pudo obtener la ubicación. Revisa los permisos del navegador.");
        setIsSubmitting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Limpieza del formulario
  const cancelarFormulario = () => {
    setCliente("");
    setClienteSeleccionado("");
    setCentro("");
    setComentarios("");
    setMensaje("");
    setDescripcionOT("");
    setCoordenadas({ latitud: null, longitud: null });
    setInicio(null);
    setOrden("");
    setCentros([]);
  };

  const botonDeshabilitado = isSubmitting || !orden || !centro || loadingCatalogos || uiLocked;

  // ---------------------------------------------------------------------------
  // Render (MUI + theme)
  // ---------------------------------------------------------------------------
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Iniciar Intervención
        </Typography>

        <Box className="contentContainer contentContainer--formSingle">
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Mensajes de validación propios del formulario */}
              {mensaje && (
                <Typography color="error" sx={{ fontSize: 14 }}>
                  {mensaje}
                </Typography>
              )}

              {/* Aviso si no hay catálogos y estás offline */}
              {!loadingCatalogos &&
                !ordenes?.length &&
                !clientes?.length &&
                !navigator.onLine && (
                  <Typography color="error" sx={{ fontSize: 14 }}>
                    No hay datos de órdenes/clientes en caché. Abre la app con conexión al menos una vez para precargarlos.
                  </Typography>
                )}

              {/* CLIENTE */}
              <Autocomplete
                size="small"
                options={clientesConOT}
                getOptionLabel={(c) => c?.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={clienteObj}
                onChange={(_, newValue) => {
                  clienteCambiadoManualmente.current = true;
                  setClienteSeleccionado(newValue ? String(newValue.id) : "");
                }}
                loading={loadingCatalogos}
                disabled={loadingCatalogos || uiLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    placeholder={loadingCatalogos ? "Cargando clientes..." : "Selecciona un cliente"}
                  />
                )}
                clearOnEscape
                noOptionsText={loadingCatalogos ? "Cargando..." : "No hay clientes disponibles"}
              />

              {/* ORDEN DE TRABAJO */}
              <Autocomplete
                size="small"
                options={ordenesOrdenadas}
                getOptionLabel={(o) => o?.titulo || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={ordenObj}
                onChange={(_, newValue) => setOrden(newValue ? String(newValue.id) : "")}
                loading={loadingCatalogos}
                disabled={loadingCatalogos || uiLocked || (!!clienteSeleccionado && !ordenesOrdenadas.length)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Orden de trabajo"
                    placeholder={loadingCatalogos ? "Cargando OTs..." : "Selecciona una OT"}
                  />
                )}
                clearOnEscape
                noOptionsText={loadingCatalogos ? "Cargando..." : "No hay OTs disponibles"}
              />

              {descripcionOT && (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  <b>Descripción:</b> {descripcionOT}
                </Typography>
              )}

              {/* CENTRO DE TRABAJO */}
              <Autocomplete
                size="small"
                options={centrosOrdenados}
                getOptionLabel={(c) => c?.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={centroObj}
                onChange={(_, newValue) => setCentro(newValue ? String(newValue.id) : "")}
                loading={loadingCatalogos}
                disabled={!orden || !centrosOrdenados.length || loadingCatalogos || uiLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Centro de trabajo"
                    placeholder={loadingCatalogos ? "Cargando..." : "Selecciona un centro"}
                    required
                  />
                )}
                clearOnEscape
                noOptionsText={loadingCatalogos ? "Cargando..." : "No hay centros disponibles"}
              />

              {/* COMENTARIOS */}
              <TextField
                label="Comentarios"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                placeholder="Opcional"
                multiline
                minRows={3}
                disabled={uiLocked}
              />

              {/* ACCIONES */}
              <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={cancelarFormulario}
                  disabled={isSubmitting || loadingCatalogos || uiLocked}
                >
                  Limpiar
                </Button>

                <Button onClick={capturarUbicacionYCrearTicaje} disabled={botonDeshabilitado} aria-busy={isSubmitting}>
                  {isSubmitting ? "Iniciando..." : "Capturar ubicación e iniciar"}
                </Button>
              </Stack>

              {/* Info de depuración (opcional) */}
              {inicio && (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Inicio local: {new Date(inicio).toLocaleString()}
                  {coordenadas.latitud ? (
                    <>
                      {" "}
                      — ({coordenadas.latitud.toFixed(5)}, {coordenadas.longitud?.toFixed(5)})
                    </>
                  ) : null}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
